import { useMemo } from "react";
import { t } from "@lingui/core/macro";
import { adminUrls } from "_/routes";

import { Buildset } from "_/data/buildsets";
import { Channel } from "_/data/channels";
import { useCurrentUserPermissions, ROOT_DOMAIN } from "_/data/rbac";

import { DateTime } from "_/components/date-time";
import { Tooltip } from "_/components/tooltip";
import { Table, ColumnDef, CustomCellLink } from "_/components/table";
import { formatBuildsetLabel } from "_/utils/buildset-display";
import { BuildsetTooltip } from "_/components/buildsetTooltip";

import { ActionsMenu } from "./actionsMenu";

type BuildsetsTableProps = {
  buildsets: Buildset[];
  channels: Channel[];
  isLoading: boolean;
};

export const BuildsetsTable = ({
  buildsets,
  channels,
  isLoading,
}: BuildsetsTableProps) => {
  const { data: rootPermissions } = useCurrentUserPermissions(ROOT_DOMAIN);

  const channelsByBuildset = useMemo(() => {
    return channels.reduce<Record<string, string[]>>((acc, c) => {
      if (c.targetBuildset) {
        acc[c.targetBuildset] = [...(acc[c.targetBuildset] ?? []), c.name];
      }
      return acc;
    }, {});
  }, [channels]);

  const columns = useMemo<ColumnDef<Buildset>[]>(
    () => [
      {
        id: "version",
        header: t`common.version`,
        accessorKey: "displayName",
        meta: { customTd: true },
        cell: ({
          row: {
            original: { id, displayName, displayVersion },
          },
        }) => {
          const {
            value,
            fullName,
            id: labelId,
          } = formatBuildsetLabel({
            id,
            displayName,
            displayVersion,
          });
          return (
            <CustomCellLink to={adminUrls.buildset(id)}>
              <BuildsetTooltip value={value} fullName={fullName} id={labelId}>
                {value}
              </BuildsetTooltip>
            </CustomCellLink>
          );
        },
      },
      {
        id: "channels",
        header: t`common.channels`,
        accessorFn: ({ id }) => (channelsByBuildset[id] ?? []).length,
        cell: ({
          row: {
            original: { id },
          },
        }) => {
          const active = channelsByBuildset[id] ?? [];
          return active.length ? active.join(", ") : "—";
        },
      },
      {
        id: "built-at",
        header: t`common.built-at`,
        accessorFn: ({ buildTime }) => new Date(buildTime).getTime(),
        meta: { customTd: true },
        cell: ({
          row: {
            original: { buildUrl, buildTime },
          },
        }) => (
          <CustomCellLink to={buildUrl}>
            <Tooltip
              target={<DateTime value={buildTime} />}
              content={buildTime}
              placement="top"
              delay={200}
              wait={200}
            />
          </CustomCellLink>
        ),
      },
      {
        id: "actions",
        header: t`common.actions`,
        cell: ({ row: { original } }) => (
          <ActionsMenu buildset={original} rootPermissions={rootPermissions} />
        ),
      },
    ],
    [channelsByBuildset, rootPermissions]
  );

  return (
    <Table
      columns={columns}
      data={buildsets}
      loading={isLoading}
      initialState={{ sorting: [{ id: "built-at", desc: true }] }}
    />
  );
};
